/**
 * Breaks — reusable break periods that can be assigned to timetables.
 *
 * Migrated from mock data to live API as part of Wave 3.2. Follows the
 * same pattern as the Organization-module pages (Departments,
 * Positions, etc): list fetch on mount + refetch on save/delete, live
 * form using backend's snake_case shape directly, ConfirmDialog for
 * delete confirmations.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutlined,
  EditOutlined,
  FreeBreakfastOutlined,
  LocalCafeOutlined,
  RefreshOutlined,
  RestaurantOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi } from '../../../api';
import type { Break, BreakCreate } from '../../../api/attendance';
import { ConfirmDialog } from '../../../components/primitives';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../i18n/translateApiError';

const ICON_MAP: Record<string, React.ReactElement> = {
  lunch: <RestaurantOutlined />,
  tea: <LocalCafeOutlined />,
  rest: <FreeBreakfastOutlined />,
};

const DURATION_OPTIONS = [5, 10, 15, 20, 30, 45, 60, 90, 120];

const EMPTY_FORM: BreakCreate = {
  name: '',
  duration_min: 30,
  start_time: '12:00',
  end_time: '12:30',
  fixed_time: true,
  type: 'unpaid',
  deduct_from_work: true,
  require_clock_in: false,
  icon: 'lunch',
};

const Breaks: React.FC = () => {
  const { t } = useTranslation();
  const [breaks, setBreaks] = useState<Break[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBreak, setEditingBreak] = useState<Break | null>(null);
  const [form, setForm] = useState<BreakCreate>({ ...EMPTY_FORM });

  const [pendingDelete, setPendingDelete] = useState<Break | null>(null);

  const fetchBreaks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await attendanceApi.breaksApi.list();
      setBreaks(data);
    } catch (e) {
      const msg =
        e instanceof ApiError ? translateApiError(e, t) : t('attendance:breaks.loadFailed');
      setError(msg);
      setBreaks([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBreaks();
  }, [fetchBreaks]);

  const handleOpen = (item?: Break) => {
    if (item) {
      setEditingBreak(item);
      setForm({
        name: item.name,
        duration_min: item.duration_min,
        start_time: item.start_time ?? '',
        end_time: item.end_time ?? '',
        fixed_time: item.fixed_time,
        type: item.type,
        deduct_from_work: item.deduct_from_work,
        require_clock_in: item.require_clock_in,
        icon: item.icon,
      });
    } else {
      setEditingBreak(null);
      setForm({ ...EMPTY_FORM });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      // Null out start/end times if we're in flexible mode — the
      // backend treats empty strings as "not set".
      const payload: BreakCreate = {
        ...form,
        start_time: form.fixed_time ? form.start_time || null : null,
        end_time: form.fixed_time ? form.end_time || null : null,
      };
      if (editingBreak) {
        await attendanceApi.breaksApi.update(editingBreak.id, payload);
        setToast(t('attendance:breaks.toastUpdated', { name: form.name }));
      } else {
        await attendanceApi.breaksApi.create(payload);
        setToast(t('attendance:breaks.toastCreated', { name: form.name }));
      }
      setDialogOpen(false);
      await fetchBreaks();
    } catch (e) {
      const msg = e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed');
      setToast(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await attendanceApi.breaksApi.delete(pendingDelete.id);
      setToast(t('attendance:breaks.toastDeleted', { name: pendingDelete.name }));
      await fetchBreaks();
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('attendance:breaks.deleteFailedReferenced');
      setToast(msg);
      // Rethrow so ConfirmDialog stays open for retry.
      throw e;
    }
  };

  const breakIcon = (icon: string) =>
    ICON_MAP[icon] ?? <FreeBreakfastOutlined />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}
          >{t('attendance:breaks.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('attendance:breaks.subtitleDetail')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchBreaks}
              disabled={loading}
              sx={{
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
          >
            {t('attendance:breaks.addBreak')}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {loading && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Break Cards */}
      {!loading && (
        <Grid container spacing={2}>
          {breaks.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'border-color 80ms ease',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => handleOpen(item)}
              >
                <CardContent sx={{ px: 2.5, py: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: 'primary.light',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'primary.main',
                          '& svg': { fontSize: '1.1rem' },
                        }}
                      >
                        {breakIcon(item.icon)}
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: 'text.primary' }}
                        >
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.fixed_time
                            ? `${item.start_time ?? '—'} – ${item.end_time ?? '—'} · ${item.duration_min} min`
                            : `${item.duration_min} min · ${t('attendance:breaks.chipFlexible')}`}
                        </Typography>
                      </Box>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpen(item);
                        }}
                        sx={{ '&:hover': { color: 'primary.main' } }}
                      >
                        <EditOutlined sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDelete(item);
                        }}
                        sx={{ '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteOutlined sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.75}>
                    <Chip
                      label={item.type === 'paid' ? t('attendance:breaks.chipPaid') : t('attendance:breaks.chipUnpaid')}
                      size="small"
                      variant="outlined"
                      color={item.type === 'paid' ? 'success' : 'default'}
                      sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
                    />
                    <Chip
                      label={item.deduct_from_work ? t('attendance:breaks.chipDeducted') : t('attendance:breaks.chipNotDeducted')}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
                    />
                    {!item.fixed_time && (
                      <Chip
                        label={t('attendance:breaks.chipFlexible')}
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {breaks.length === 0 && !error && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <FreeBreakfastOutlined
                  sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('attendance:breaks.noBreaks')}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpen()}
                  sx={{
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                  }}
                >
                  {t('attendance:breaks.addFirst')}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* Add/Edit Dialog — stays bespoke because it has a complex form
          with fixed/flexible mode toggle. The shared ConfirmDialog only
          covers yes/no prompts. */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ m: 0, py: 2, fontWeight: 600, fontSize: '1rem' }}>
          {editingBreak ? t('attendance:breaks.editTitle') : t('attendance:breaks.addTitle')}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              label={t('attendance:breaks.nameLabel')}
              value={form.name}
              onChange={(e) =>
                setForm((p) => ({ ...p, name: e.target.value }))
              }
              size="small"
              fullWidth
              placeholder={t('attendance:breaks.namePlaceholder')}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!form.fixed_time}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, fixed_time: e.target.checked }))
                  }
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  {t('attendance:breaks.fixedTime')}
                </Typography>
              }
            />
            {form.fixed_time && (
              <Stack direction="row" spacing={1.5}>
                <TextField
                  label={t('attendance:breaks.startTime')}
                  type="time"
                  value={form.start_time ?? ''}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, start_time: e.target.value }))
                  }
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label={t('attendance:breaks.endTime')}
                  type="time"
                  value={form.end_time ?? ''}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, end_time: e.target.value }))
                  }
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel>{t('attendance:breaks.duration')}</InputLabel>
              <Select
                value={form.duration_min}
                label={t('attendance:breaks.durationLabel')}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    duration_min: Number(e.target.value),
                  }))
                }
              >
                {DURATION_OPTIONS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m >= 60 ? `${m / 60} hour${m > 60 ? 's' : ''}` : `${m} min`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('attendance:breaks.typeLabel')}</InputLabel>
              <Select
                value={form.type ?? 'unpaid'}
                label={t('attendance:breaks.typeLabel')}
                onChange={(e) =>
                  setForm((p) => ({ ...p, type: e.target.value as string }))
                }
              >
                <MenuItem value="paid">{t('attendance:breaks.paidDesc')}</MenuItem>
                <MenuItem value="unpaid">
                  {t('attendance:breaks.unpaidDesc')}
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('attendance:breaks.iconLabel')}</InputLabel>
              <Select
                value={form.icon ?? 'rest'}
                label={t('attendance:breaks.iconLabel')}
                onChange={(e) =>
                  setForm((p) => ({ ...p, icon: e.target.value as string }))
                }
              >
                <MenuItem value="lunch">{t('attendance:breaks.iconLunch')}</MenuItem>
                <MenuItem value="tea">{t('attendance:breaks.iconTea')}</MenuItem>
                <MenuItem value="rest">{t('attendance:breaks.iconRest')}</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!form.deduct_from_work}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      deduct_from_work: e.target.checked,
                    }))
                  }
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  {t('attendance:breaks.deductFromWork')}
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!form.require_clock_in}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      require_clock_in: e.target.checked,
                    }))
                  }
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    {t('attendance:breaks.requireClockIn')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem' }}
                  >
                    {t('attendance:breaks.requireClockInHint')}
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            size="small"
            sx={{ borderRadius: 1, textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            size="small"
            disabled={!form.name?.trim() || saving}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {saving ? (
              <CircularProgress size={18} />
            ) : editingBreak ? (
              t('system:shared.saveChanges')
            ) : (
              t('attendance:breaks.addBreak')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('attendance:breaks.deleteTitle')}
        description={t('attendance:breaks.deleteDescription')}
        subject={
          pendingDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {pendingDelete.name}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Breaks;
